import React from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  BookOpen,
  CircleHelp,
  Eye,
  Heart,
  ListChecks,
  MessageCircle,
  PlusCircle,
  RefreshCw,
  ShoppingBag,
  Sparkles,
  Utensils,
  WandSparkles,
} from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../ui/collapsible";

const sectionShell = "scroll-mt-28 rounded-3xl border p-6 sm:p-8 shadow-sm";

/** Section title with optional leading icon (decorative). */
function SectionHeading({ icon: Icon, children, className = "" }) {
  return (
    <h2 className={`font-serif text-2xl text-forest flex items-center gap-2.5 flex-wrap ${className}`}>
      {Icon ? <Icon className="w-5 h-5 text-gold/90 shrink-0" strokeWidth={1.5} aria-hidden /> : null}
      <span>{children}</span>
    </h2>
  );
}

const WHY_CARD = [
  { key: "routine", label: "Routine", tone: "bg-[#e8ede9] border-[#d5ddd7] text-forest" },
  { key: "taste", label: "Taste", tone: "bg-[#f3ead8] border-[#e5d9bc] text-forest" },
  { key: "pairing", label: "Pairing", tone: "bg-[#e9e6f2] border-[#d8d3e6] text-forest" },
];

/**
 * @param {{ ing: Record<string, unknown> & { library: Record<string, unknown>, name: string, whyAddToSeaMoss?: string, bestWith?: string, suggestedBases?: string, routineTags?: string[] } }} props
 */
export default function IngredientLibraryContent({ ing }) {
  const L = ing.library;
  if (!L) return null;

  const why = L.whyPeopleChooseIt || {};
  const detox = L.detoxFraming;
  const nutrients = L.nutrientsOrCompounds || [];
  const firstNutrient = nutrients[0];
  const restNutrients = nutrients.slice(1);
  const faqs = L.faqs || [];
  const pairings = L.pairings || [];
  const glance = L.glance || { bullets: [] };
  const community = L.communityNotes || { intro: "", placeholders: [] };

  return (
    <div className="space-y-8 md:space-y-10 mb-14">
      <section id="library-glance" className={`${sectionShell} border-gold/35 bg-[#faf8f3]`}>
        <SectionHeading icon={Eye}>{glance.title || `${ing.name} at a glance`}</SectionHeading>
        <ul className="mt-4 space-y-2.5 list-none p-0 m-0">
          {(glance.bullets || []).map((b) => (
            <li key={b} className="flex gap-2.5 text-sm text-botanical/85 leading-snug">
              <span className="text-gold shrink-0 mt-0.5" aria-hidden>
                ●
              </span>
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </section>

      <section id="library-tradition" className={`${sectionShell} border-cream bg-[#eef2ee]`}>
        <SectionHeading icon={BookOpen}>Traditional use</SectionHeading>
        <div className="mt-4 space-y-3 text-sm text-botanical/85 leading-relaxed max-w-3xl">
          {(L.traditionalParagraphs || []).map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>
      </section>

      <section id="library-why" className="scroll-mt-28">
        <SectionHeading className="mb-4" icon={Heart}>
          Why people choose it
        </SectionHeading>
        <p className="text-sm text-botanical/75 mb-5 max-w-2xl">
          Three quick lenses: routine mood, spoon taste, and what it sits nicely beside. Not medical advice.
        </p>
        <ul className="grid md:grid-cols-3 gap-4 list-none p-0 m-0">
          {WHY_CARD.map((c) => (
            <li key={c.key} className={`rounded-2xl border p-5 ${c.tone}`}>
              <h3 className="font-serif text-lg text-forest">{c.label}</h3>
              <p className="mt-2 text-sm leading-relaxed opacity-95">{why[c.key] || ""}</p>
            </li>
          ))}
        </ul>
      </section>

      <section id="library-nutrients" className={`${sectionShell} border-gold/30 bg-[#fbf6ec]`}>
        <SectionHeading icon={ListChecks}>Nutrients and plant compounds</SectionHeading>
        {firstNutrient ? <p className="mt-3 text-sm text-botanical/85 leading-relaxed max-w-3xl">{firstNutrient}</p> : null}
        {restNutrients.length > 0 ? (
          <Collapsible className="mt-4">
            <CollapsibleTrigger
              type="button"
              className="text-sm font-medium text-forest underline-offset-4 hover:underline cursor-pointer bg-transparent border-0 p-0 text-left"
            >
              Read a little more
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-3 space-y-3 text-sm text-botanical/80 leading-relaxed max-w-3xl">
              {restNutrients.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </CollapsibleContent>
          </Collapsible>
        ) : null}
      </section>

      {detox?.show && detox.paragraphs?.length > 0 ? (
        <section id="library-detox" className={`${sectionShell} border-cream bg-offwhite`}>
          <SectionHeading icon={RefreshCw}>{detox.title || `Does ${ing.name} support detox?`}</SectionHeading>
          <div className="mt-4 space-y-3 text-sm text-botanical/85 leading-relaxed max-w-3xl">
            {detox.paragraphs.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        </section>
      ) : null}

      <section id="library-taste" className={`${sectionShell} border-cream bg-[#fdfbf7]`}>
        <SectionHeading icon={Utensils}>How it tastes in sea moss gel</SectionHeading>
        <div className="mt-4 space-y-3 text-sm text-botanical/85 leading-relaxed max-w-3xl">
          {(L.tasteInGel || []).map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>
        {(ing.whyAddToSeaMoss || ing.bestWith) && (
          <div className="mt-6 rounded-2xl border border-cream bg-white/80 p-5">
            <h3 className="font-serif text-lg text-forest flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-gold/90 shrink-0" strokeWidth={1.5} aria-hidden />
              <span>Why add it to sea moss gel?</span>
            </h3>
            {ing.whyAddToSeaMoss ? <p className="mt-2 text-sm text-botanical/85 leading-relaxed">{ing.whyAddToSeaMoss}</p> : null}
            {ing.bestWith ? (
              <p className="mt-3 text-sm text-botanical/85">
                <strong className="text-forest">Pairs well with:</strong> {ing.bestWith}
              </p>
            ) : null}
            {ing.suggestedBases ? (
              <p className="mt-2 text-xs text-botanical/65">
                Suggested bases:{" "}
                {ing.suggestedBases === "both" ? "Gold or Purple" : ing.suggestedBases === "gold" ? "Gold" : "Purple"}.
              </p>
            ) : null}
            {ing.routineTags?.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {ing.routineTags.map((t) => (
                  <span key={t} className="rounded-full bg-cream px-3 py-1 text-[11px] text-forest">
                    {t}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </section>

      <section id="library-pairings" className="scroll-mt-28">
        <SectionHeading className="mb-3" icon={PlusCircle}>
          Best pairings
        </SectionHeading>
        <p className="text-sm text-botanical/75 mb-5 max-w-2xl">Taste-led ideas for the Moss Matrix builder. Adjust amounts to your own palate.</p>
        <ul className="flex flex-wrap gap-2.5 list-none p-0 m-0">
          {pairings.map((p) => (
            <li
              key={p.label}
              className="rounded-2xl border border-gold/25 bg-cream/40 px-4 py-3 max-w-xs sm:max-w-sm shadow-sm"
              title={p.caution || undefined}
            >
              <div className="font-serif text-sm text-forest leading-snug">{p.label}</div>
              <p className="mt-1.5 text-xs text-botanical/80 leading-snug">{p.reason}</p>
              {p.caution ? <p className="mt-2 text-[11px] text-forest/90 border-t border-gold/20 pt-2">Caution: {p.caution}</p> : null}
            </li>
          ))}
        </ul>
      </section>

      <section id="library-community" className={`${sectionShell} border-gold/25 bg-[#faf8f3]`}>
        <SectionHeading icon={MessageCircle}>From the community</SectionHeading>
        <p className="mt-3 text-sm text-botanical/85 leading-relaxed max-w-3xl">{community.intro}</p>
        <ul className="mt-6 grid sm:grid-cols-3 gap-3 list-none p-0 m-0">
          {(community.placeholders || []).map((line) => (
            <li
              key={line}
              className="rounded-2xl border border-dashed border-cream bg-white/60 px-4 py-5 text-center text-sm text-botanical/60"
            >
              {line}
            </li>
          ))}
        </ul>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link
            to="/stories"
            className="inline-flex items-center justify-center gap-2 rounded-full border border-forest/20 bg-white px-5 py-2.5 text-sm font-medium text-forest hover:border-gold/50 transition"
          >
            <MessageCircle className="w-3.5 h-3.5 text-gold/90 shrink-0" strokeWidth={1.65} aria-hidden />
            Tell your story
          </Link>
          <Link
            to="/mix-ins"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-forest text-cream px-5 py-2.5 text-sm font-medium hover:bg-botanical transition"
          >
            <WandSparkles className="w-3.5 h-3.5 text-gold shrink-0" strokeWidth={1.65} aria-hidden />
            Explore mix-in ideas
          </Link>
        </div>
        {/* TODO: pre-select this ingredient in a future mix-ins / builder flow via URL hash or query once share/hydrate supports ingredient id. */}
      </section>

      {L.cautionBullets?.length > 0 ? (
        <section id="library-cautions" className={`${sectionShell} border-[#e8d9c4] bg-[#faf5ec]`}>
          <SectionHeading icon={AlertTriangle}>Cautions</SectionHeading>
          {ing.cautions ? <p className="mt-3 text-sm text-botanical/85 leading-relaxed max-w-3xl">{ing.cautions}</p> : null}
          <p className="mt-3 text-sm text-forest font-medium">Avoid or seek advice first if you:</p>
          <ul className="mt-3 list-disc pl-5 space-y-2 text-sm text-botanical/85 max-w-3xl">
            {L.cautionBullets.map((b) => (
              <li key={b}>{b}</li>
            ))}
          </ul>
          <p className="mt-4 text-xs text-botanical/75 max-w-3xl">
            Food amounts and concentrated herbal use are not the same thing. We do not give child dosage advice on this page. If you are unsure, pause
            use and speak with a qualified healthcare professional.
          </p>
        </section>
      ) : null}

      {faqs.length > 0 ? (
        <section id="library-faq" className={`${sectionShell} border-cream bg-white`}>
          <SectionHeading className="mb-4" icon={CircleHelp}>
            Quick answers
          </SectionHeading>
          <div className="space-y-2 max-w-3xl">
            {faqs.map((item) => (
              <details
                key={item.q}
                className="group rounded-2xl border border-cream bg-offwhite open:bg-white transition-colors"
              >
                <summary className="cursor-pointer list-none px-4 py-3 font-medium text-forest text-sm flex items-center justify-between gap-3 [&::-webkit-details-marker]:hidden">
                  <span>{item.q}</span>
                  <span className="text-gold text-xs shrink-0 group-open:rotate-180 transition-transform" aria-hidden>
                    ▼
                  </span>
                </summary>
                <div className="px-4 pb-4 text-sm text-botanical/85 leading-relaxed border-t border-cream/80 pt-3">{item.a}</div>
              </details>
            ))}
          </div>
        </section>
      ) : null}

      <nav className="rounded-2xl border border-cream bg-offwhite px-4 py-3 text-xs text-botanical/80" aria-label="Helpful links">
        <span className="font-medium text-forest">Explore Moss Matrix: </span>
        <Link to="/#choose-sea-moss" className="underline hover:text-forest">
          Choose your sea moss
        </Link>
        {" · "}
        <Link to="/shop" className="underline hover:text-forest">
          Shop
        </Link>
        {" · "}
        <Link to="/ingredients" className="underline hover:text-forest">
          Ingredients
        </Link>
        {" · "}
        <Link to="/blends" className="underline hover:text-forest">
          Blend routines
        </Link>
        {" · "}
        <Link to="/learn/how-to-use-sea-moss-gel" className="underline hover:text-forest">
          How to use sea moss gel
        </Link>
        {" · "}
        <Link to="/faq" className="underline hover:text-forest">
          FAQ
        </Link>
      </nav>

      {L.ctaLinks?.length > 0 ? (
        <section className="rounded-3xl border border-gold/40 bg-gradient-to-br from-cream/60 to-white p-8 text-center sm:text-left">
          <p className="text-xs uppercase tracking-widest text-botanical/60 mb-3">Next steps</p>
          <div className="flex flex-col sm:flex-row flex-wrap gap-3">
            {L.ctaLinks.map((c) => {
              const isBuilder = c.href.includes("builder");
              const Ico = isBuilder ? WandSparkles : ShoppingBag;
              return (
                <Link
                  key={c.href + c.label}
                  to={c.href}
                  className="inline-flex justify-center items-center gap-2 rounded-full border border-forest/20 bg-white px-5 py-2.5 text-sm font-medium text-forest hover:border-gold/50 hover:bg-cream/50 transition"
                >
                  <Ico className="w-3.5 h-3.5 text-gold/90 shrink-0" strokeWidth={1.65} aria-hidden />
                  {c.label}
                </Link>
              );
            })}
          </div>
        </section>
      ) : null}
    </div>
  );
}
